import { ApolloClient, ApolloLink, from, HttpLink } from '@apollo/client'

import { tokenUtils } from '../utils/token-utils'
import { currentWorkspaceVar } from '../utils/update-workspace-headers'
import { cache } from './apollo-cache'
import { envConfig } from './env-config'
import { errorLink } from './error-link'

interface CustomHeaders {
  authorization: string
  handle?: string
  'ws-id'?: string
  'Ws-Id'?: string
}

const httpLink = new HttpLink({
  uri: envConfig.graphqlUrl
})

const authMiddleware = new ApolloLink((operation, forward) => {
  // Get the headers
  const { headers = {} } = operation.getContext()

  // Get the authentication token
  const token = tokenUtils.getToken('Access-Token')

  // Initialize headers
  const customHeaders: CustomHeaders = {
    authorization: token || ''
  }

  // Only run on client-side
  if (typeof window !== 'undefined') {
    // Check if ws-id is already provided in the operation context (highest priority)
    // This allows components to override the workspace ID if needed
    const contextWorkspaceId = headers['ws-id'] || headers['Ws-Id']

    if (contextWorkspaceId) {
      // Use the workspace ID from the operation context (highest priority)
      customHeaders['ws-id'] = contextWorkspaceId
      customHeaders['Ws-Id'] = contextWorkspaceId
    } else {
      // Get workspace ID from reactive variable (source of truth)
      // IMPORTANT: Read the reactive variable fresh on each operation to get the latest value
      // The reactive variable is updated by:
      // 1. workspace-switcher.tsx when user manually switches workspace
      // 2. useWorkspaceSelection hook when URL changes
      // 3. workspace-selection utilities when workspace state changes
      // We trust this value completely and don't fall back to cache reads to avoid stale data
      const workspaceId = currentWorkspaceVar()

      if (workspaceId) {
        customHeaders['ws-id'] = workspaceId
        customHeaders['Ws-Id'] = workspaceId
      }
    }
  }

  // Set the headers
  operation.setContext({
    headers: {
      ...headers,
      ...customHeaders
    }
  })

  return forward(operation)
})

// Create the Apollo Client
export const client = new ApolloClient({
  cache: cache,
  connectToDevTools: true,
  link: from([errorLink, authMiddleware, httpLink])
})
